/**
 * mentorRejection — single source of truth for the per-reason UX copy
 * shown to a mentor whose application was rejected.
 *
 * For every backend ``rejection_reason`` code (the seven TextChoices on
 * ``MentorApplication.RejectionReason``) this module exposes three layers
 * of text, from least to most specific:
 *
 *   1. ``REJECTION_TITLES[code]``       — short headline (chip / banner / toast).
 *   2. ``REJECTION_DESCRIPTIONS[code]`` — 1-2 sentences "what didn't match".
 *   3. ``REJECTION_TIPS[code]``         — 2-4 action-oriented bullets.
 *
 * The exact wording was reviewed with the design:ux-copy skill: empathetic,
 * concrete, action-oriented — we tell the mentor what we want to see, not
 * what they did wrong (see ``docs/audit/2026-05-28-rejection-ux.md`` §6 for
 * the rationale). Keep the seven codes here in lockstep with
 * ``backend/PlatformaAPP/users/models.py::MentorApplication.RejectionReason``.
 *
 * The OTHER bucket intentionally keeps an empty description — for ``other``
 * the moderator's free-form ``moderation_comment`` carries the actual
 * signal, and the rejected-section in BecomeMentorForm renders that comment
 * directly without wrapping it in a template.
 */
import type { RejectionReasonCode } from '@/shared/types/mentor';

export const REJECTION_TITLES: Record<RejectionReasonCode, string> = {
    spam: 'Профиль не подходит под тематику платформы',
    low_quality: 'Описание профиля слишком короткое',
    irrelevant: 'Профиль не подходит под тематику платформы',
    misleading: 'Описание содержит сомнительные обещания',
    inappropriate: 'Фото или данные требуют замены',
    no_experience: 'Опыт ещё не подтверждён',
    other: 'Заявка требует индивидуального разбора',
};

export const REJECTION_DESCRIPTIONS: Record<RejectionReasonCode, string> = {
    spam:
        'Платформа фокусируется на наставничестве в IT, продукте, дизайне, маркетинге '
        + 'и смежных областях. Ваш профиль либо вне этих тем, либо похож на дубль '
        + 'другого зарегистрированного аккаунта.',
    low_quality:
        'Из вашего описания пока сложно понять, чем именно вы можете быть полезны '
        + 'ученикам. Нам нужны конкретика и контекст — кому, в каких задачах '
        + 'и в каком формате вы помогаете.',
    irrelevant:
        'Платформа фокусируется на наставничестве в IT, продукте, дизайне, маркетинге '
        + 'и смежных областях. Ваш профиль либо вне этих тем, либо похож на дубль '
        + 'другого зарегистрированного аккаунта.',
    misleading:
        'Мы заметили в профиле формулировки, которые сложно подтвердить и которые '
        + 'могут ввести учеников в заблуждение (гарантия результата, ссылки на '
        + 'громкие компании без верификации, обещания трудоустройства).',
    inappropriate:
        'Мы не смогли опубликовать профиль из-за фото или данных, которые не '
        + 'подходят под правила платформы (низкое качество, нерелевантный контент '
        + 'или несоответствие реальной личности).',
    no_experience:
        'Нам нужно больше доказательств вашего опыта — без этого ученикам сложно '
        + 'сделать выбор, а нам — гарантировать качество.',
    other:
        'Модератор оставил подробный комментарий ниже — прочитайте его и внесите '
        + 'правки. Если что-то непонятно, напишите в поддержку, ссылаясь на номер '
        + 'заявки.',
};

export const REJECTION_TIPS: Record<RejectionReasonCode, readonly string[]> = {
    spam: [
        'Сформулируйте тему так, чтобы по ней было понятно, с каким запросом к вам приходить.',
        'Если профиль повторяет ваш предыдущий — напишите в поддержку, мы объединим аккаунты.',
        'Если тема релевантна, но звучит слишком общо — уточните нишу (например, «карьерное развитие → SRE в финтехе»).',
    ],
    low_quality: [
        'Расширьте раздел «О себе» — минимум 300 знаков, расскажите о специализации и подходе.',
        'Добавьте 2-3 темы, в которых сильны больше всего.',
        'Опишите формат работы: разовые консультации, программы наставничества, разбор задач.',
    ],
    irrelevant: [
        'Сформулируйте тему так, чтобы по ней было понятно, с каким запросом к вам приходить.',
        'Если профиль повторяет ваш предыдущий — напишите в поддержку, мы объединим аккаунты.',
        'Если тема релевантна, но звучит слишком общо — уточните нишу (например, «карьерное развитие → SRE в финтехе»).',
    ],
    misleading: [
        'Уберите формулировки «гарантирую», «100 % результат», «работа за 30 дней» — замените на конкретный опыт.',
        'Если упоминаете работодателя — добавьте ссылку на LinkedIn или профиль на сайте компании.',
        'Опишите, что именно вы делаете, а не «что получит ученик».',
    ],
    inappropriate: [
        'Загрузите профессиональное фото лица в хорошем разрешении (не логотип, не аватар-мем).',
        'Проверьте, что имя и фамилия совпадают с теми, под которыми вы публично выступаете.',
        'Уберите из bio контактные данные «в обход» платформы (мессенджеры, личные номера).',
    ],
    no_experience: [
        'Добавьте 2+ места работы с датами и должностями в разделе «Опыт».',
        'Заполните соц-сети (LinkedIn / Хабр / GitHub) — это сильнейший публичный сигнал.',
        'Если есть кейсы, проекты, выступления — приложите ссылки в описание.',
    ],
    other: [
        'Прочитайте комментарий модератора — в нём подробности по вашему случаю.',
        'Если что-то непонятно — напишите в поддержку, ссылаясь на номер заявки.',
        'Внесите правки и подайте заново — у вас остаются попытки в пределах лимита.',
    ],
};

const VALID_CODES = new Set<RejectionReasonCode>([
    'spam',
    'low_quality',
    'irrelevant',
    'misleading',
    'inappropriate',
    'no_experience',
    'other',
]);

function isKnown(code: string | undefined | null): code is RejectionReasonCode {
    return typeof code === 'string' && VALID_CODES.has(code as RejectionReasonCode);
}

/**
 * Safe accessor — returns the per-code title or an empty string if the
 * incoming code is unknown (e.g. backend grew a new reason that the frontend
 * doesn't recognise yet). Empty string lets the caller fall back to a generic
 * banner without crashing.
 */
export function getRejectionTitle(code: RejectionReasonCode | string | undefined | null): string {
    if (!isKnown(code)) return '';
    return REJECTION_TITLES[code];
}

export function getRejectionDescription(
    code: RejectionReasonCode | string | undefined | null,
): string {
    if (!isKnown(code)) return '';
    return REJECTION_DESCRIPTIONS[code];
}

export function getRejectionTips(
    code: RejectionReasonCode | string | undefined | null,
): readonly string[] {
    if (!isKnown(code)) return [];
    return REJECTION_TIPS[code];
}

export type { RejectionReasonCode };
